import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Edit, Trash2, TrendingUp, Calendar, Gauge, Battery, CheckCircle2, XCircle, Star, Crown, AlertCircle, CreditCard, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoteDialog, setPromoteDialog] = useState({ open: false, listing: null });
  const [selectedPackage, setSelectedPackage] = useState('premium_1');
  const [packages, setPackages] = useState({});
  const [dealOfDayStatus, setDealOfDayStatus] = useState({ available: true, remaining_days: 0 });
  const [processing, setProcessing] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadListings();
    loadDealOfDayStatus();
    loadPackages();
    
    // Check for payment return
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      checkPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Platba bola zrušená');
      // Clear URL params
      setSearchParams({});
    }
  }, []);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/payments/status/${sessionId}`, {
          credentials: 'include'
        });
        
        if (!res.ok) throw new Error('Failed to check status');
        
        const data = await res.json();
        
        if (data.status === 'paid') {
          toast.success(data.message || 'Platba úspešná! Inzerát je propagovaný.');
          setCheckingPayment(false);
          setSearchParams({});
          loadListings();
          loadDealOfDayStatus();
          return;
        } else if (data.status === 'expired') {
          toast.error('Platba vypršala');
          setCheckingPayment(false);
          setSearchParams({});
          return;
        }
        
        // Still pending
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.info('Platba sa spracováva. Stav bude aktualizovaný.');
          setCheckingPayment(false);
          setSearchParams({});
        }
      } catch (error) {
        console.error('Payment check error:', error);
        setCheckingPayment(false);
        setSearchParams({});
      }
    };
    
    poll();
  };

  const loadPackages = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/promotion-packages`);
      const data = await res.json();
      setPackages(data);
    } catch (error) {
      console.error('Failed to load packages:', error);
    }
  };

  const loadDealOfDayStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/listings/deal-of-day-status`);
      const data = await res.json();
      setDealOfDayStatus(data);
    } catch (error) {
      console.error('Failed to load deal of day status:', error);
    }
  };

  const loadListings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/listings`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to load listings');
      const data = await res.json();
      setListings(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load listings:', error);
      toast.error('Nepodarilo sa načítať inzeráty');
      setLoading(false);
    }
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('Naozaj chcete zmazať tento inzerát?')) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/listings/${listingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to delete');

      toast.success('Inzerát bol zmazaný');
      loadListings();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Nepodarilo sa zmazať inzerát');
    }
  };

  const handlePromote = async () => {
    if (!promoteDialog.listing || !selectedPackage) return;
    setProcessing(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          listing_id: promoteDialog.listing.listing_id,
          package_id: selectedPackage,
          origin_url: window.location.origin
        })
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Chyba pri vytváraní platby');
      }

      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error('Failed to create checkout:', error);
      toast.error(error.message || 'Nepodarilo sa vytvoriť platbu');
      setProcessing(false);
    }
  };

  const isPromoted = (listing) => {
    return listing.promoted_until && new Date(listing.promoted_until) > new Date();
  };

  const getPromotionTypeLabel = (listing) => {
    if (!isPromoted(listing)) return null;
    if (listing.promotion_type === 'deal_of_day') return { label: 'Ponuka dňa', color: 'bg-green-600' };
    return { label: 'Premium', color: 'bg-amber-500' };
  };

  const getPackagesByType = (type) => {
    return Object.entries(packages)
      .filter(([key, pkg]) => pkg.type === type)
      .map(([key, pkg]) => ({ id: key, ...pkg }));
  };

  const isDealPackageDisabled = () => {
    return !dealOfDayStatus.available && 
           dealOfDayStatus.listing_id !== promoteDialog.listing?.listing_id;
  };

  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        {checkingPayment && <p className="text-slate-600">Overujem platbu...</p>}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-primary" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="dashboard-heading">
              Moje inzeráty
            </h1>
            <Button
              data-testid="create-listing-button"
              onClick={() => navigate('/create-listing')}
              className="bg-accent text-white hover:bg-lime-600 font-semibold"
            >
              + Nový inzerát
            </Button>
          </div>

          {listings.length === 0 ? (
            <Card className="p-12 text-center">
              <Battery className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Zatiaľ nemáte žiadne inzeráty</h3>
              <p className="text-slate-600 mb-4">Vytvorte svoj prvý inzerát a začnite predávať</p>
              <Button
                onClick={() => navigate('/create-listing')}
                className="bg-primary text-white hover:bg-slate-800"
              >
                Vytvoriť inzerát
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="listings-grid">
              {listings.map(listing => (
                <Card key={listing.listing_id} className="overflow-hidden border border-slate-200" data-testid={`listing-card-${listing.listing_id}`}>
                  <div className="aspect-video bg-slate-100 relative overflow-hidden">
                    {listing.images && listing.images.length > 0 ? (
                      <img
                        src={listing.images[0]}
                        alt={`${listing.brand_name} ${listing.model_name}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Battery className="h-16 w-16 text-slate-300" />
                      </div>
                    )}
                    {isPromoted(listing) && (
                      <Badge className="absolute top-2 right-2 bg-accent text-white" data-testid="promoted-badge">
                        Propagované
                      </Badge>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-xl font-bold text-primary mb-1" style={{fontFamily: 'Manrope, sans-serif'}}>
                      {listing.brand_name} {listing.model_name}
                    </h3>
                    <p className="text-2xl font-bold text-accent mb-2" style={{fontFamily: 'Manrope, sans-serif'}}>
                      €{listing.price.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      {listing.sms_verified ? (
                        <Badge className="bg-green-100 text-green-800 text-xs" data-testid="verified-badge">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Overené
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-800 text-xs" data-testid="unverified-badge">
                          <XCircle className="h-3 w-3 mr-1" />
                          Neoverené
                        </Badge>
                      )}
                      <Badge className={`text-xs ${listing.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}`}>
                        {listing.status === 'active' ? 'Aktívne' : 'Neaktívne'}
                      </Badge>
                      {getPromotionTypeLabel(listing) && (
                        <Badge className={`text-xs text-white ${getPromotionTypeLabel(listing).color}`}>
                          {listing.promotion_type === 'deal_of_day' ? <Crown className="h-3 w-3 mr-1" /> : <Star className="h-3 w-3 mr-1" />}
                          {getPromotionTypeLabel(listing).label}
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm text-slate-600 mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {listing.year}
                      </div>
                      <div className="flex items-center gap-1">
                        <Gauge className="h-4 w-4" />
                        {(listing.mileage / 1000).toFixed(0)}k km
                      </div>
                      <div className="flex items-center gap-1">
                        <Battery className="h-4 w-4" />
                        {listing.power} kW
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        data-testid={`edit-button-${listing.listing_id}`}
                        onClick={() => navigate(`/edit-listing/${listing.listing_id}`)}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Upraviť
                      </Button>
                      <Button
                        data-testid={`promote-button-${listing.listing_id}`}
                        onClick={() => {
                          setPromoteDialog({ open: true, listing });
                          setSelectedPackage('premium_1');
                        }}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <TrendingUp className="h-4 w-4 mr-1" />
                        Propagovať
                      </Button>
                      <Button
                        data-testid={`delete-button-${listing.listing_id}`}
                        onClick={() => handleDelete(listing.listing_id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      <Dialog open={promoteDialog.open} onOpenChange={(open) => setPromoteDialog({ open, listing: promoteDialog.listing })}>
        <DialogContent data-testid="promote-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Propagovať inzerát
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Premium packages */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Star className="h-5 w-5 text-amber-500" />
                <Label className="text-base font-semibold">Premium</Label>
              </div>
              <p className="text-sm text-slate-500 mb-3">Zobrazí sa v sekcii Premium inzeráty (max 10)</p>
              <RadioGroup value={selectedPackage} onValueChange={setSelectedPackage} className="grid grid-cols-3 gap-2">
                {getPackagesByType('premium').map(pkg => (
                  <div key={pkg.id}>
                    <RadioGroupItem value={pkg.id} id={pkg.id} className="peer sr-only" />
                    <Label 
                      htmlFor={pkg.id} 
                      className="flex flex-col items-center justify-center p-3 border-2 rounded-lg cursor-pointer hover:bg-amber-50 peer-data-[state=checked]:border-amber-500 peer-data-[state=checked]:bg-amber-50"
                    >
                      <span className="font-bold text-lg">{pkg.days} {pkg.days === 1 ? 'deň' : 'dni'}</span>
                      <span className="text-amber-600 font-semibold">€{pkg.price}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Deal of the day packages */}
            <div className={isDealPackageDisabled() ? 'opacity-50' : ''}>
              <div className="flex items-center gap-2 mb-3">
                <Crown className="h-5 w-5 text-green-600" />
                <Label className="text-base font-semibold">Ponuka dňa</Label>
                {isDealPackageDisabled() && (
                  <Badge variant="outline" className="text-red-600 border-red-200">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Obsadené ({dealOfDayStatus.remaining_days} dní)
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-500 mb-3">Exkluzívna pozícia na vrchu hlavnej stránky</p>
              <RadioGroup 
                value={selectedPackage} 
                onValueChange={setSelectedPackage} 
                className="grid grid-cols-3 gap-2"
                disabled={isDealPackageDisabled()}
              >
                {getPackagesByType('deal_of_day').map(pkg => (
                  <div key={pkg.id}>
                    <RadioGroupItem 
                      value={pkg.id} 
                      id={pkg.id} 
                      className="peer sr-only" 
                      disabled={isDealPackageDisabled()}
                    />
                    <Label 
                      htmlFor={pkg.id} 
                      className={`flex flex-col items-center justify-center p-3 border-2 rounded-lg ${
                        isDealPackageDisabled() 
                          ? 'cursor-not-allowed bg-slate-100' 
                          : 'cursor-pointer hover:bg-green-50 peer-data-[state=checked]:border-green-500 peer-data-[state=checked]:bg-green-50'
                      }`}
                    >
                      <span className="font-bold text-lg">{pkg.days} {pkg.days === 1 ? 'deň' : 'dni'}</span>
                      <span className="text-green-600 font-semibold">€{pkg.price}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Selected package summary */}
            {packages[selectedPackage] && (
              <div className="p-4 bg-slate-50 rounded-lg border">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{packages[selectedPackage].label}</p>
                    <p className="text-sm text-slate-500">
                      {packages[selectedPackage].type === 'deal_of_day' ? 'Exkluzívna pozícia' : 'Premium sekcia'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">€{packages[selectedPackage].price}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setPromoteDialog({ open: false, listing: null })}
                disabled={processing}
                className="flex-1"
              >
                Zrušiť
              </Button>
              <Button
                data-testid="confirm-promote-button"
                onClick={handlePromote}
                disabled={processing || (packages[selectedPackage]?.type === 'deal_of_day' && isDealPackageDisabled())}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Presmerovanie...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Zaplatiť €{packages[selectedPackage]?.price || 0}
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-center text-slate-400">
              Platba prebieha cez zabezpečenú platobnú bránu Stripe
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

export default DashboardPage;
